<template>
  <div class="dc-window" :class="{ 'dc-window--expanded': !chat.isMinimized, 'dc-window--new': chat.isNew }">
    <!-- Header / Card minimizada -->
    <div class="dc-header" @click="onHeaderClick">
      <!-- Avatar -->
      <div class="dc-avatar-wrap">
        <div v-if="chat.avatar" class="dc-avatar">
          <img :src="chat.avatar" :alt="chat.title" />
        </div>
        <div v-else class="dc-avatar dc-avatar--placeholder">
          {{ initials }}
        </div>
        <span class="dc-status-dot"></span>
      </div>

      <!-- Info -->
      <div class="dc-header-info">
        <span class="dc-header-title">{{ chat.title }}</span>
        <span class="dc-header-preview" v-if="chat.isMinimized">
          {{ chat.lastMessage || 'Sin mensajes aún' }}
        </span>
      </div>

      <!-- Controles -->
      <div class="dc-header-actions">
        <!-- Badge de no leídos -->
        <span v-if="chat.unreadCount > 0 && chat.isMinimized" class="dc-badge">
          {{ chat.unreadCount > 9 ? '9+' : chat.unreadCount }}
        </span>
        <!-- Hora -->
        <span class="dc-header-time" v-if="chat.isMinimized && chat.lastMessageAt">
          {{ formatTime(chat.lastMessageAt) }}
        </span>
        <!-- Botón minimizar -->
        <button class="dc-btn-icon" @click.stop="onToggleMinimize" :title="chat.isMinimized ? 'Expandir' : 'Minimizar'">
          <svg v-if="!chat.isMinimized" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="18 15 12 9 6 15"></polyline>
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
        <!-- Botón cerrar -->
        <button class="dc-btn-icon dc-btn-icon--close" @click.stop="onClose" title="Cerrar chat">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>

    <!-- Cuerpo del chat (solo visible cuando no está minimizado) -->
    <transition name="dc-slide">
      <div v-if="!chat.isMinimized" class="dc-body">
        <!-- Área de mensajes -->
        <div class="dc-messages" ref="messagesContainer" @scroll="onScroll">
          <!-- Loader de paginación -->
          <div v-if="chat.isLoading && chat.messages.length > 0" class="dc-load-more">
            <div class="dc-spinner-sm"></div>
          </div>

          <!-- Botón cargar más -->
          <div v-if="chat.hasMore && !chat.isLoading" class="dc-load-more">
            <button class="dc-load-btn" @click="loadMore">Cargar mensajes anteriores</button>
          </div>

          <!-- Estado vacío / Loading inicial -->
          <div v-if="chat.isLoading && chat.messages.length === 0" class="dc-empty">
            <div class="dc-spinner"></div>
            <span>Cargando mensajes...</span>
          </div>
          <div v-else-if="chat.messages.length === 0 && !chat.isLoading" class="dc-empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            <span>Sé el primero en escribir</span>
          </div>

          <!-- Mensajes -->
          <div
            v-for="(msg, idx) in chat.messages"
            :key="msg.id || idx"
            class="dc-msg-row"
            :class="{ 'dc-msg-row--mine': isMyMessage(msg), 'dc-msg-row--optimistic': msg.isOptimistic }"
          >
            <!-- Avatar solo para mensajes ajenos -->
            <div v-if="!isMyMessage(msg)" class="dc-msg-avatar">
              <div v-if="chat.avatar" class="dc-msg-avatar-img">
                <img :src="chat.avatar" :alt="chat.title" />
              </div>
              <div v-else class="dc-msg-avatar-img dc-msg-avatar-img--placeholder">
                {{ initials }}
              </div>
            </div>

            <div class="dc-msg-bubble-wrap">
              <div class="dc-msg-bubble">
                <p class="dc-msg-content">{{ msg.content }}</p>
              </div>
              <span class="dc-msg-time">
                {{ formatTime(msg.createdAt) }}
                <svg v-if="msg.isOptimistic" class="dc-sending-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </span>
            </div>
          </div>
        </div>

        <!-- Input de mensaje -->
        <div class="dc-input-area">
          <textarea
            v-model="newMessage"
            class="dc-input"
            placeholder="Escribe un mensaje..."
            rows="1"
            @keydown.enter.exact.prevent="sendMessage"
            @input="autoGrow"
            ref="textarea"
            :disabled="!chat.id"
          ></textarea>
          <button
            class="dc-send-btn"
            :disabled="!newMessage.trim() || !chat.id"
            @click="sendMessage"
            title="Enviar (Enter)"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'DirectChatWindow',

  props: {
    chat: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      newMessage: ''
    }
  },

  computed: {
    ...mapGetters(['getProfile']),

    initials () {
      const parts = (this.chat.title || '?').split(' ')
      return parts.slice(0, 2).map(p => p[0]).join('').toUpperCase()
    }
  },

  watch: {
    'chat.isMinimized' (minimized) {
      if (!minimized) {
        this.$nextTick(() => {
          this.scrollToBottom()
          this.markRead()
          if (this.chat.messages.length === 0 && this.chat.id) {
            this.loadMessages({ chatLocalId: this.chat.localId, chatId: this.chat.id })
          }
        })
      }
    },
    'chat.messages' () {
      if (!this.chat.isMinimized) {
        this.$nextTick(() => this.scrollToBottom())
      }
    }
  },

  mounted () {
    if (!this.chat.isMinimized) {
      this.$nextTick(() => {
        this.scrollToBottom()
        if (this.chat.messages.length === 0 && this.chat.id) {
          this.loadMessages({ chatLocalId: this.chat.localId, chatId: this.chat.id })
        }
      })
    }
  },

  methods: {
    onHeaderClick () {
      if (this.chat.isMinimized) {
        this.onToggleMinimize()
      }
    },

    onToggleMinimize () {
      this.$store.dispatch('directChats/toggleMinimize', this.chat.localId)
    },

    onClose () {
      this.$store.dispatch('directChats/closeChat', this.chat.localId)
    },

    markRead () {
      this.$store.dispatch('directChats/markAsRead', this.chat.localId)
    },

    async sendMessage () {
      if (!this.newMessage.trim() || !this.chat.id) return
      const content = this.newMessage
      this.newMessage = ''
      this.$nextTick(() => {
        if (this.$refs.textarea) {
          this.$refs.textarea.style.height = 'auto'
        }
      })
      await this.$store.dispatch('directChats/sendMessage', {
        chatLocalId: this.chat.localId,
        content
      })
    },

    loadMessages ({ chatLocalId, chatId }) {
      this.$store.dispatch('directChats/loadMessages', { chatLocalId, chatId })
    },

    loadMore () {
      this.$store.dispatch('directChats/loadMoreMessages', this.chat.localId)
    },

    isMyMessage (msg) {
      // El mensaje es mío si el sender coincide con el profile del usuario o es optimístico
      if (msg.isOptimistic) return true
      const profile = this.getProfile
      if (!profile) return false
      // Comparar por id de usuario
      return msg.sender === profile.id ||
        (msg.senderData && msg.senderData.id === profile.id)
    },

    formatTime (iso) {
      if (!iso) return ''
      const d = new Date(iso)
      const now = new Date()
      const diffDays = Math.floor((now - d) / 86400000)
      if (diffDays === 0) {
        return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
      } else if (diffDays === 1) {
        return 'Ayer'
      } else {
        return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })
      }
    },

    scrollToBottom () {
      const el = this.$refs.messagesContainer
      if (el) el.scrollTop = el.scrollHeight
    },

    onScroll (e) {
      if (e.target.scrollTop === 0 && this.chat.hasMore && !this.chat.isLoading) {
        const prevHeight = e.target.scrollHeight
        this.loadMore()
        this.$nextTick(() => {
          e.target.scrollTop = e.target.scrollHeight - prevHeight
        })
      }
    },

    autoGrow (e) {
      e.target.style.height = 'auto'
      e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px'
    }
  }
}
</script>

<style scoped>
/* ===== VARIABLES ===== */
.dc-window {
  --dc-primary: #4F46E5;
  --dc-primary-light: #6366F1;
  --dc-primary-dark: #3730A3;
  --dc-surface: #ffffff;
  --dc-surface-2: #F8FAFC;
  --dc-border: #E2E8F0;
  --dc-text: #1E293B;
  --dc-text-muted: #64748B;
  --dc-text-light: #94A3B8;
  --dc-shadow: 0 4px 24px rgba(0, 0, 0, 0.10), 0 1px 4px rgba(0, 0, 0, 0.06);
  --dc-shadow-hover: 0 8px 32px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.08);
  --dc-radius: 16px;
  --dc-radius-msg: 18px;
  --dc-transition: 0.22s cubic-bezier(0.4, 0, 0.2, 1);

  position: relative;
  width: 280px;
  background: var(--dc-surface);
  border-radius: var(--dc-radius);
  box-shadow: var(--dc-shadow);
  transition: box-shadow var(--dc-transition), transform var(--dc-transition);
  overflow: hidden;
  flex-shrink: 0;
}

.dc-window:hover {
  box-shadow: var(--dc-shadow-hover);
}

/* Animación de entrada para chat nuevo */
.dc-window--new .dc-header {
  animation: dc-pulse 0.6s ease-out;
}

@keyframes dc-pulse {
  0% { box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.5); }
  70% { box-shadow: 0 0 0 8px rgba(79, 70, 229, 0); }
  100% { box-shadow: 0 0 0 0 rgba(79, 70, 229, 0); }
}

/* ===== HEADER ===== */
.dc-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: linear-gradient(135deg, var(--dc-primary) 0%, var(--dc-primary-light) 100%);
  cursor: pointer;
  user-select: none;
  min-height: 60px;
}

.dc-header:hover {
  background: linear-gradient(135deg, var(--dc-primary-dark) 0%, var(--dc-primary) 100%);
}

/* ===== AVATAR ===== */
.dc-avatar-wrap {
  position: relative;
  flex-shrink: 0;
}

.dc-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(255,255,255,0.3);
}

.dc-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dc-avatar--placeholder {
  background: rgba(255,255,255,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.5px;
}

.dc-status-dot {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  background: #10B981;
  border-radius: 50%;
  border: 2px solid #fff;
}

/* ===== HEADER INFO ===== */
.dc-header-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.dc-header-title {
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: 'Inter', 'Roboto', sans-serif;
}

.dc-header-preview {
  font-size: 11.5px;
  color: rgba(255,255,255,0.7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: 'Inter', 'Roboto', sans-serif;
}

/* ===== HEADER ACTIONS ===== */
.dc-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.dc-header-time {
  font-size: 10px;
  color: rgba(255,255,255,0.65);
  font-family: 'Inter', 'Roboto', sans-serif;
}

.dc-badge {
  background: #EF4444;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  border-radius: 10px;
  padding: 1px 5px;
  min-width: 18px;
  text-align: center;
  line-height: 16px;
  animation: dc-bounce 0.5s ease;
}

@keyframes dc-bounce {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
}

.dc-btn-icon {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: none;
  background: rgba(255,255,255,0.12);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background var(--dc-transition);
  padding: 0;
}

.dc-btn-icon svg {
  width: 14px;
  height: 14px;
  stroke: rgba(255,255,255,0.85);
}

.dc-btn-icon:hover {
  background: rgba(255,255,255,0.22);
}

.dc-btn-icon--close:hover {
  background: rgba(239, 68, 68, 0.5);
}

/* ===== BODY ===== */
.dc-body {
  display: flex;
  flex-direction: column;
  height: 340px;
}

/* ===== SLIDE TRANSITION ===== */
.dc-slide-enter-active,
.dc-slide-leave-active {
  transition: max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease;
  overflow: hidden;
  max-height: 400px;
}

.dc-slide-enter,
.dc-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

/* ===== MESSAGES ===== */
.dc-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px 12px 6px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--dc-surface-2);
  scroll-behavior: smooth;
}

.dc-messages::-webkit-scrollbar {
  width: 4px;
}

.dc-messages::-webkit-scrollbar-track {
  background: transparent;
}

.dc-messages::-webkit-scrollbar-thumb {
  background: var(--dc-border);
  border-radius: 2px;
}

/* ===== EMPTY STATE ===== */
.dc-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--dc-text-muted);
  padding: 20px;
  text-align: center;
}

.dc-empty svg {
  width: 36px;
  height: 36px;
  opacity: 0.35;
}

.dc-empty span {
  font-size: 12.5px;
  font-family: 'Inter', 'Roboto', sans-serif;
}

/* ===== SPINNERS ===== */
.dc-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--dc-border);
  border-top-color: var(--dc-primary);
  border-radius: 50%;
  animation: dc-spin 0.7s linear infinite;
}

.dc-spinner-sm {
  width: 16px;
  height: 16px;
  border: 2px solid var(--dc-border);
  border-top-color: var(--dc-primary);
  border-radius: 50%;
  animation: dc-spin 0.7s linear infinite;
}

@keyframes dc-spin {
  to { transform: rotate(360deg); }
}

/* ===== LOAD MORE ===== */
.dc-load-more {
  display: flex;
  justify-content: center;
  padding: 4px 0;
}

.dc-load-btn {
  background: none;
  border: 1px solid var(--dc-border);
  color: var(--dc-primary);
  font-size: 11px;
  border-radius: 20px;
  padding: 4px 12px;
  cursor: pointer;
  font-family: 'Inter', 'Roboto', sans-serif;
  transition: background var(--dc-transition);
}

.dc-load-btn:hover {
  background: rgba(79, 70, 229, 0.06);
}

/* ===== MESSAGE ROW ===== */
.dc-msg-row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  animation: dc-msg-in 0.2s ease-out;
}

@keyframes dc-msg-in {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

.dc-msg-row--mine {
  flex-direction: row-reverse;
}

.dc-msg-row--optimistic .dc-msg-bubble {
  opacity: 0.7;
}

/* ===== MESSAGE AVATAR ===== */
.dc-msg-avatar {
  flex-shrink: 0;
}

.dc-msg-avatar-img {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
}

.dc-msg-avatar-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dc-msg-avatar-img--placeholder {
  background: var(--dc-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  color: #fff;
}

/* ===== BUBBLE ===== */
.dc-msg-bubble-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 70%;
  gap: 2px;
}

.dc-msg-row--mine .dc-msg-bubble-wrap {
  align-items: flex-end;
}

.dc-msg-bubble {
  background: #fff;
  border: 1px solid var(--dc-border);
  border-radius: var(--dc-radius-msg);
  border-bottom-left-radius: 4px;
  padding: 8px 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.dc-msg-row--mine .dc-msg-bubble {
  background: linear-gradient(135deg, var(--dc-primary) 0%, var(--dc-primary-light) 100%);
  border: none;
  border-radius: var(--dc-radius-msg);
  border-bottom-right-radius: 4px;
  box-shadow: 0 2px 8px rgba(79, 70, 229, 0.25);
}

.dc-msg-content {
  font-size: 13px;
  line-height: 1.45;
  color: var(--dc-text);
  margin: 0;
  word-break: break-word;
  font-family: 'Inter', 'Roboto', sans-serif;
}

.dc-msg-row--mine .dc-msg-content {
  color: #fff;
}

.dc-msg-time {
  font-size: 10px;
  color: var(--dc-text-light);
  display: flex;
  align-items: center;
  gap: 3px;
  font-family: 'Inter', 'Roboto', sans-serif;
}

.dc-sending-icon {
  width: 11px;
  height: 11px;
  stroke: var(--dc-text-light);
}

/* ===== INPUT AREA ===== */
.dc-input-area {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 12px;
  background: var(--dc-surface);
  border-top: 1px solid var(--dc-border);
}

.dc-input {
  flex: 1;
  background: var(--dc-surface-2);
  border: 1.5px solid var(--dc-border);
  border-radius: 12px;
  padding: 8px 12px;
  font-size: 13px;
  font-family: 'Inter', 'Roboto', sans-serif;
  color: var(--dc-text);
  resize: none;
  outline: none;
  transition: border-color var(--dc-transition);
  line-height: 1.4;
  max-height: 96px;
  overflow-y: auto;
}

.dc-input:focus {
  border-color: var(--dc-primary);
  background: #fff;
}

.dc-input::placeholder {
  color: var(--dc-text-light);
}

.dc-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dc-send-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--dc-primary), var(--dc-primary-light));
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: opacity var(--dc-transition), transform var(--dc-transition);
}

.dc-send-btn svg {
  width: 18px;
  height: 18px;
  fill: #fff;
}

.dc-send-btn:hover:not(:disabled) {
  transform: scale(1.05);
  opacity: 0.9;
}

.dc-send-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
